/**
 * Resolves a `claim_id` to the thing an editor actually wants to see: the affirmation, who said
 * it, when, and the verbatim line from the transcript.
 *
 * Two sources, deliberately in this order:
 *
 * 1. **The normalized claim** (signals) — the only one carrying `evidence.excerpt`, the literal
 *    words from the guion. That is the strongest provenance the system has.
 * 2. **The prenota claim** — the story's own copy. No verbatim excerpt, but it resolves ids the
 *    signals do not, because the two stages did not always mint the same key.
 *
 * A draft can cite an id that neither has (see `hasIncompatibleClaimIds`). That case returns
 * `null` and the UI says so rather than rendering an empty box — Art Direction §7.
 */

import type { EditorialPrenota, EditorialSignal } from 'src/types/editorial';

export type ResolvedClaim = {
  claimId: string;
  /** The canonical affirmation — machine judgment, set in the interface face. */
  text: string;
  speaker: string;
  timestamp: string;
  /** Verbatim from the guion — testimony, set in the reading face. Absent on prenota-only claims. */
  excerpt: string | null;
  claimType: string;
  modality: string;
  source: 'normalized' | 'prenota';
};

export function buildClaimResolver(
  signals: EditorialSignal[],
  prenotas: EditorialPrenota[]
): Map<string, ResolvedClaim> {
  const index = new Map<string, ResolvedClaim>();

  // Prenotas first, so a normalized claim overwrites them — it carries the excerpt.
  for (const prenota of prenotas) {
    for (const claim of prenota.payload.claims) {
      if (!claim.claim_id || index.has(claim.claim_id)) continue;
      index.set(claim.claim_id, {
        claimId: claim.claim_id,
        text: claim.claim,
        speaker: claim.speaker,
        timestamp: claim.timestamp,
        excerpt: null,
        claimType: claim.claim_type ?? '',
        modality: '',
        source: 'prenota',
      });
    }
  }

  for (const signal of signals) {
    for (const claim of signal.payload.normalized_claims) {
      index.set(claim.claim_id, {
        claimId: claim.claim_id,
        text: claim.claim_text,
        speaker: claim.speaker.name,
        timestamp: claim.evidence.timestamps[0] ?? '',
        excerpt: claim.evidence.excerpt,
        claimType: claim.claim_type,
        modality: claim.modality,
        source: 'normalized',
      });
    }
  }

  return index;
}
